import { useEffect, useRef, useState } from "react";
import { Camera, Download, FileText, Image as ImageIcon, Paperclip, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { download } from "@/lib/api";
import { fmtSize, type Attachment } from "@/lib/briefs";

const ACCEPT = ".pdf,.docx,.xlsx,.csv,.pptx,.png,.jpg,.jpeg,.heic,image/png,image/jpeg,image/heic";

/**
 * ⚠ The personal-data reminder (SPEC §3). Shown once per brief before the first
 * file picker opens, and repeated in one line next to the controls. It is the
 * house rule stated where it applies, not a filter.
 */
export const PD_REMINDER =
  "Please don't upload personal or sensitive data (names with NRIC, pay, medical details). Replace it with dummy values: the structure is what matters.";

function reminderKey(briefId: string) {
  return `brief-pd-reminder-${briefId}`;
}

type Source = "file" | "photo" | "camera";

/** True from the `sm` breakpoint up, where a file picker is the natural first step. */
function useWide(): boolean {
  const query = "(min-width: 640px)";
  const read = () => (typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(query).matches : true);
  const [wide, setWide] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(query);
    const on = () => setWide(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return wide;
}

/**
 * The one way a file gets picked, wherever the button is: the personal-data
 * reminder once per brief, then the hidden input for a file, a photo from the
 * library, or the camera. `open` remembers which box the file is for, so the
 * caller's box can change while the picker is up without moving the file.
 * Render `elements` once next to the button.
 */
export function useFilePicker(briefId: string, onPick: (file: File, sectionId: string | null) => void) {
  const refs = { file: useRef<HTMLInputElement>(null), photo: useRef<HTMLInputElement>(null), camera: useRef<HTMLInputElement>(null) };
  const target = useRef<string | null>(null);
  const pick = useRef(onPick);
  pick.current = onPick;
  const [reminder, setReminder] = useState<Source | null>(null);

  const seen = () => {
    try {
      return localStorage.getItem(reminderKey(briefId)) === "1";
    } catch {
      return true;
    }
  };
  const open = (which: Source, sectionId: string | null) => {
    target.current = sectionId;
    if (!seen()) {
      setReminder(which);
      return;
    }
    refs[which].current?.click();
  };
  const acknowledge = () => {
    try {
      localStorage.setItem(reminderKey(briefId), "1");
    } catch {
      /* private mode */
    }
    const which = reminder;
    setReminder(null);
    if (which) setTimeout(() => refs[which].current?.click(), 0);
  };
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) pick.current(file, target.current);
  };

  const elements = (
    <>
      <input ref={refs.file} data-source="file" type="file" accept={ACCEPT} className="hidden" onChange={onChange} />
      <input ref={refs.photo} data-source="photo" type="file" accept="image/*" className="hidden" onChange={onChange} />
      <input ref={refs.camera} data-source="camera" type="file" accept="image/*" capture="environment" className="hidden" onChange={onChange} />
      <Dialog open={reminder !== null} onClose={() => setReminder(null)} title="Before you attach a file">
        <p className="text-sm leading-relaxed">{PD_REMINDER}</p>
        <p className="text-muted-foreground mt-2 text-sm">
          A spreadsheet's column names and a few sample rows are what the AI reads; the rest stays here.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setReminder(null)}>
            Cancel
          </Button>
          <Button onClick={acknowledge}>Understood, choose a file</Button>
        </div>
      </Dialog>
    </>
  );
  return { open, elements };
}

export function AttachControls({
  briefId,
  sectionId,
  onPick,
  busy,
  compact = false,
}: {
  briefId: string;
  sectionId: string | null;
  onPick: (file: File, sectionId: string | null) => void;
  busy: boolean;
  compact?: boolean;
}) {
  const picker = useFilePicker(briefId, onPick);
  return (
    <>
      {picker.elements}
      <Button variant={compact ? "ghost" : "outline"} size="sm" disabled={busy} onClick={() => picker.open("file", sectionId)} aria-label="Attach a file">
        <Paperclip size={14} /> {compact ? <span className="sm:hidden">Attach</span> : "Attach a file"}
      </Button>
      <Button variant={compact ? "ghost" : "outline"} size="sm" disabled={busy} onClick={() => picker.open("camera", sectionId)} className="sm:hidden" aria-label="Take a photo">
        <Camera size={14} /> {compact ? "Photo" : "Take a photo"}
      </Button>
    </>
  );
}

const CHOICES: Array<{ source: Source; label: string; icon: typeof Camera }> = [
  { source: "camera", label: "Take a photo", icon: Camera },
  { source: "photo", label: "Choose a photo", icon: ImageIcon },
  { source: "file", label: "Choose a file", icon: FileText },
];

/**
 * The paperclip in the chat composer. On a phone it asks how (camera, photo
 * library, file); from `sm` up it opens the file picker straight away. The
 * file goes to `sectionId` (the box in focus) or the whole brief.
 */
export function ComposerAttach({
  briefId,
  sectionId,
  disabled,
  onPick,
}: {
  briefId: string;
  sectionId: string | null;
  disabled: boolean;
  onPick: (file: File, sectionId: string | null) => void;
}) {
  const picker = useFilePicker(briefId, onPick);
  const wide = useWide();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // The menu closes on a click elsewhere or Escape, like the techniques menu.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      {picker.elements}
      <Button
        type="button"
        variant="outline"
        size="icon"
        disabled={disabled}
        aria-label="Attach a file or photo"
        aria-haspopup={wide ? undefined : "menu"}
        aria-expanded={wide ? undefined : open}
        title="Attach a file or photo"
        onClick={() => (wide ? picker.open("file", sectionId) : setOpen((v) => !v))}
        className="h-11 w-11 rounded-full"
      >
        <Paperclip size={16} />
      </Button>
      {open ? (
        <div role="menu" className="bg-card border-border absolute bottom-full left-0 z-20 mb-2 w-56 rounded-lg border p-1 shadow-xl">
          {CHOICES.map(({ source, label, icon: Icon }) => (
            <button
              key={source}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                picker.open(source, sectionId);
              }}
              className="hover:bg-accent flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm"
            >
              <Icon size={15} className="text-muted-foreground shrink-0" />
              {label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function AttachmentList({
  briefId,
  items,
  canEdit,
  onDelete,
  busy,
}: {
  briefId: string;
  items: Attachment[];
  canEdit: boolean;
  onDelete: (id: string) => void;
  busy: boolean;
}) {
  if (items.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1">
      {items.map((a) => (
        <li key={a.id} className="bg-secondary/60 flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs">
          <FileText size={14} className="text-muted-foreground shrink-0" />
          <span className="min-w-0 flex-1 truncate" title={a.filename}>
            {a.filename}
          </span>
          <span className="text-muted-foreground shrink-0">
            {fmtSize(a.size)}
            {a.rowCount !== null ? ` · ${a.rowCount} rows` : ""}
            {!a.hasText && !a.mime.startsWith("image/") ? " · no text read" : ""}
          </span>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground shrink-0"
            aria-label={`Download ${a.filename}`}
            onClick={() => void download(`/briefs/${briefId}/attachments/${a.id}/download`, a.filename)}
          >
            <Download size={14} />
          </button>
          {canEdit ? (
            <button
              type="button"
              className="text-muted-foreground hover:text-destructive shrink-0"
              aria-label={`Remove ${a.filename}`}
              disabled={busy}
              onClick={() => onDelete(a.id)}
            >
              <Trash2 size={14} />
            </button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
